import { AxiosResponse } from "axios";
import { call, delay, put, select, takeLatest } from "redux-saga/effects";
import { toast } from "sonner";
import CatchBlockFunction from "../../../../components/hooks/CatchBlockFunction";
import {
  deleteType,
  get,
  patch,
  post,
  putType,
} from "../../../../utils/request";
import {
  selectFormData,
  selectOrderBy,
  selectOrderDir,
  selectPageNo,
  selectPageSize,
  selectSearch,
  selectToggleLoading,
} from "./selector";
import { t } from "i18next";
import { IS_RETAILER } from "../../../../utils/Api";
import { chargesForEnum, IData, IItems } from "../types";
import { actions } from "./slice";

export function* doCreateRequest(action: { payload: { callback: any } }) {
  yield delay(500);

  const form: IData = yield select(selectFormData);
  if (form?.stateId?.length === 0) {
    toast.error(t("shipping.validation.state"));
    return;
  }
  if (form?.priceItems?.length === 0) {
    toast.error(t("shipping.validation.itemsSelect"));
    return;
  }
  const isRetailer = IS_RETAILER;
  const data = {
    chargesFor:
      isRetailer === "true" ? chargesForEnum.RETAILER : chargesForEnum.USER,
    stateId: form.stateId,
    priceItems: form.priceItems,
  };
  try {
    yield put(actions.setButtonLoading(true));
    const response: AxiosResponse = yield call(
      post,
      `admin/shipping/charges/add`,
      data
    );
    yield put(actions.setButtonLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    toast.success(response.data.message);
    yield call(action?.payload?.callback());
  } catch (error: any) {
    yield put(actions.setButtonLoading(false));
    CatchBlockFunction(error);
  }
}
export function* doGetListRequest() {
  yield delay(500);

  try {
    const isRetailer = IS_RETAILER;
    const pageNo: number = yield select(selectPageNo);
    const pageSize: number = yield select(selectPageSize);
    const orderBy: string = yield select(selectOrderBy);
    const orderDir: string = yield select(selectOrderDir);
    const search: string = yield select(selectSearch);
    const buttonLoading: boolean = yield select(selectToggleLoading);
    if (!buttonLoading) {
      yield put(actions.setLoading(true));
    }

    const response: AxiosResponse = yield call(
      get,
      `admin/shipping/charges/list/${
        isRetailer === "true" ? chargesForEnum.RETAILER : chargesForEnum.USER
      }?pageNo=${pageNo}&pageSize=${pageSize}${
        search.length > 0 ? "&search=" + search : ""
      }${orderBy.length > 0 ? "&orderBy=" + orderBy : ""}${
        orderDir.length > 0 ? "&orderDirection=" + orderDir : ""
      }`
    );
    yield put(actions.setLoading(false));
    yield put(actions.setToggleLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    yield put(
      actions.setTotalRow(
        response.data.data.meta ? response.data.data.meta.total : 0
      )
    );
    yield put(actions.setList(response.data.data.results));
  } catch (error: any) {
    yield put(actions.setLoading(false));
    yield put(actions.setToggleLoading(false));
    CatchBlockFunction(error);
  }
}
export function* doGetByIdRequest(action: { payload: string }) {
  yield delay(500);

  try {
    yield put(actions.setLoading(true));
    const response: AxiosResponse = yield call(
      get,
      `admin/shipping/charges/get/${
        action.payload !== undefined && action.payload !== null
          ? action.payload
          : ""
      }`
    );
    yield put(actions.setLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    yield put(actions.setDataForm(response.data.data));
  } catch (error: any) {
    yield put(actions.setLoading(false));
    CatchBlockFunction(error);
  }
}
export function* doUpdateRequest(action: { payload: { callback: any } }) {
  yield delay(500);

  const form: IData = yield select(selectFormData);
  if (form?.stateId?.length === 0) {
    toast.error(t("shipping.validation.state"));
    return;
  }
  if (
    form?.priceItems?.filter((x) => !x.remove)?.length == 0 ||
    form?.priceItems?.length === 0
  ) {
    toast.error(t("shipping.validation.itemsSelect"));
    return;
  }

  const data = {
    stateId: form.stateId,
    priceItems: form.priceItems,
  };
  try {
    yield put(actions.setButtonLoading(true));
    const response: AxiosResponse = yield call(
      putType,
      `admin/shipping/charges/update/${form._id}`,
      data
    );
    yield put(actions.setButtonLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    toast.success(response.data.message);
    yield call(action?.payload?.callback());
  } catch (error: any) {
    yield put(actions.setButtonLoading(false));
    CatchBlockFunction(error);
  }
}

export function* doUpdateStatusRequest(action: {
  payload: { id: string; callback: any };
}) {
  yield delay(500);
  try {
    yield put(actions.setButtonLoading(true));
    yield put(actions.setToggleLoading(true));
    const response: AxiosResponse = yield call(
      patch,
      `admin/Shipping/status/update/${
        action.payload.id !== undefined && action.payload.id !== null
          ? action.payload.id
          : ""
      }`,
      {}
    );
    yield put(actions.setButtonLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    toast.success(response.data.message);
    yield call(action.payload.callback());
  } catch (error: any) {
    yield put(actions.setButtonLoading(false));
    CatchBlockFunction(error);
  }
}

export function* doGetStateListRequest() {
  yield delay(500);

  try {
    const response: AxiosResponse = yield call(get, `admin/state/all`);
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }

    yield put(actions.setStateList(response.data.data));
  } catch (error: any) {
    CatchBlockFunction(error);
  }
}

export function* doCreateItemsRequest(action: {
  payload: { callback: () => void };
}) {
  yield delay(500);

  const form: IData = yield select(selectFormData);

  if (Number(form?.priceItemsForm?.min) === 0) {
    toast.error("Please enter minimum quantity");
    toast.error(t("shipping.validation.items.minimumQuantity"));
    return;
  }
  if (Number(form?.priceItemsForm?.max) === 0) {
    toast.error(t("shipping.validation.items.maximumQuantity"));
    return;
  }
  if (Number(form?.priceItemsForm?.max) <= Number(form?.priceItemsForm?.min)) {
    toast.error(t("shipping.validation.items.validMaxQuantity"));
    return;
  }
  if (Number(form?.priceItemsForm?.price) === 0) {
    toast.error(t("shipping.validation.items.price"));
    return;
  }

  const data = {
    priceItemsForm: form.priceItemsForm,
    min: Number(form.priceItemsForm.min),
    max: Number(form.priceItemsForm.max),
    price: Number(form.priceItemsForm.price),
  };

  try {
    yield put(actions.setItemsLoading(true));
    yield put(actions.setItemsFromData(data));
    yield put(actions.setItemsLoading(false));
    toast.success("item created successfully");
    yield call(action?.payload?.callback);
  } catch (error: any) {
    yield put(actions.setItemsLoading(false));
    CatchBlockFunction(error);
  }
}
export function* doUpdateItemsRequest(action: {
  payload: { index: number; data: IItems; callback: () => void };
}) {
  yield delay(500);

  // const form: IData = yield select(selectFormData);
  const form = action.payload.data;

  if (Number(form?.min) === 0) {
    toast.error("Please enter minimum quantity");
    toast.error(t("shipping.validation.items.minimumQuantity"));
    return;
  }
  if (Number(form?.max) === 0) {
    toast.error(t("shipping.validation.items.maximumQuantity"));
    return;
  }
  if (Number(form?.max) <= Number(form?.min)) {
    toast.error(t("shipping.validation.items.validMaxQuantity"));
    return;
  }
  if (Number(form?.price) === 0) {
    toast.error(t("shipping.validation.items.price"));
    return;
  }

  const data = {
    min: Number(form.min),
    max: Number(form.max),
    price: Number(form.price),
  };

  try {
    yield put(actions.setItemsLoading(true));
    yield put(
      actions.updateItemsFromData({ index: action.payload.index, data: data })
    );
    yield put(actions.setItemsLoading(false));
    toast.success("item update successfully");
    yield call(action?.payload?.callback);
  } catch (error: any) {
    yield put(actions.setItemsLoading(false));
    CatchBlockFunction(error);
  }
}

export function* doDeleteRequest(action: {
  payload: { id: string; callback: () => void };
}) {
  yield delay(500);
  try {
    yield put(actions.setButtonLoading(true));
    const response: AxiosResponse = yield call(
      deleteType,
      `admin/shipping/charges/delete/${action.payload.id}`
    );
    yield put(actions.setButtonLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    toast.success(response.data.message);
    yield call(action?.payload?.callback);
  } catch (error: any) {
    yield put(actions.setButtonLoading(false));
    CatchBlockFunction(error);
  }
}
export function* ShippingRepoSaga() {
  yield takeLatest(actions.doCreate, doCreateRequest);
  yield takeLatest(actions.doUpdate, doUpdateRequest);
  yield takeLatest(actions.doGetList, doGetListRequest);
  yield takeLatest(actions.doGetById, doGetByIdRequest);
  yield takeLatest(actions.doGetStateList, doGetStateListRequest);
  yield takeLatest(actions.doCreateItems, doCreateItemsRequest);
  yield takeLatest(actions.doUpdateItems, doUpdateItemsRequest);
  yield takeLatest(actions.doDelete, doDeleteRequest);
}
